// ============================================================
// HOUSE OF CARDS — App v0.2 — THE FULL HOUSE
// Screen router + story player + manor controller.
// title → story(prologue → yearsLater → mystic) → manor → dead
// All content lives in storyScript.js / roomData.js / puzzleEngine.js.
// v0.2: pressHotspot dispatch (clock/drain/books), take re-take guard,
//       puzzle results can launch story scripts (the finale),
//       story beats "flag" and "trophy" supported.
// ============================================================
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./App.css";
import { RM, MANOR_BG, FIRE_BG, emberGlow } from "./theme";
import { SCRIPTS } from "./storyScript";
import { ROOMS, ITEMS } from "./roomData";
import { pressHotspot, tryDoor, resolveUse, resolveChoice } from "./puzzleEngine";
import { decideEnding, applyDrives, recordEnding } from "./endingsEngine";
import QTE from "./QTEngine";
import SceneEngine from "./SceneEngine";
import Inventory from "./Inventory";
import PsychosisEngine, { PSY_TIERS } from "./PsychosisEngine";
import Jumpscare, { useJumpscareScheduler } from "./JumpscareEngine";
import MainMenu from "./MainMenu";
import { newGameState, loadGame, saveGame, saveCheckpoint, loadCheckpoint, wipeSave } from "./saveManager";
import Audio from "./AudioManager";
import { heartbeatTierForPct, stingForScare } from "./AudioAssets";

const DUSK_BG = [
  "radial-gradient(ellipse at 50% 100%, rgba(110,35,51,0.35) 0%, transparent 60%)",
  "radial-gradient(ellipse at 50% -20%, rgba(46,36,16,0.5) 0%, transparent 60%)",
  "#0a0807",
].join(", ");
const BGS = { fire: FIRE_BG, dusk: DUSK_BG, manor: MANOR_BG };

export default function App() {
  const [state, setState] = useState(() => {
    const loaded = loadGame();
    if (loaded) {
      // Front-of-house every launch: land on the menu, keep all progress.
      return {
        ...loaded,
        drives: loaded.drives || {},
        settings: loaded.settings || { motion: true, ambient: true, muted: false },
        screen: "menu",
      };
    }
    return newGameState();
  });

  // A real save = the player has actually started (entered the manor or unlocked a door).
  const hasSave = state.sawManorIntro || state.unlockedDoors.length > 0 || state.deaths > 0;

  // story player internals (not saved — story replays cleanly)
  const [scriptId, setScriptId] = useState(null);
  const [beatIdx, setBeatIdx] = useState(0);
  const [bg, setBg] = useState("dusk");
  const [qteFail, setQteFail] = useState(null); // failText shown on death-in-story

  // manor internals
  const [armedItem, setArmedItem] = useState(null);
  const [toast, setToast] = useState(null);          // {text, trophy?}
  const [tenantReset, setTenantReset] = useState(0); // bump to reset timer
  const [psyReduce, setPsyReduce] = useState({ n: 0, frac: 0.5 }); // bump n to half-clear (cursed shroom)
  const [scareFlash, setScareFlash] = useState(false);
  const [retryScript, setRetryScript] = useState(null); // died mid-story → retry that scene
  const [psyTier, setPsyTier] = useState(0);         // 0 calm · 1 fraying · 2 breaking
  const [eyesOn, setEyesOn] = useState(false);       // Unstable Eyes view toggled on?
  const [overlayOpen, setOverlayOpen] = useState(false); // a closeup/text box is open in the room
  const [psyPct, setPsyPct] = useState(0);           // raw psychosis % (heartbeat + mushroom clear)
  const [mushroomUsed, setMushroomUsed] = useState(false); // shroom eaten this cycle? (once-per-"day")
  const [raving, setRaving] = useState(false);       // mushroom rave in progress (pauses psychosis)
  const [starPower, setStarPower] = useState(false); // finale theme up?

  // A jumpscare FLASH: red flash + optional shake. Non-lethal — this is
  // the lightweight sting fired by puzzle/choice/egg results & hotspot.scare.
  const fireScare = useCallback(() => {
    setScareFlash(true);
    setTimeout(() => setScareFlash(false), 450);
  }, []);

  // ── THE REAL JUMPSCARES (SVG fiends) ─────────────────────────
  // The full SCARE_BANK scheduler, finally mounted. It arms a timer
  // while the player is exploring the manor (no overlay open) and,
  // when it fires, takes over the screen with a fiend + single-key QTE.
  // Frequency scales with the psychosis tier: the higher Tome's
  // psychosis, the sooner scares come — in EVERY mode. Cursed Mode
  // tightens the baseline further on top of that.
  const jumpscare = useJumpscareScheduler({
    roomId: state.currentRoom,
    unlockedCount: state.unlockedDoors.length,
    cursed: state.cursed,
    enabled: true,
  });

  // persist
  useEffect(() => saveGame(state), [state]);

  // ── AUDIO: mute toggle (respects Settings) ───────────────────
  useEffect(() => {
    Audio.setMuted(!!state.settings?.muted);
  }, [state.settings?.muted]);

  // ── AUDIO: menu bed vs. the house ────────────────────────────
  // On the menu screen the menu loop plays and the house bed is
  // paused. Leaving the menu closes it; the room + heartbeat effects
  // below restart whatever the manor was doing.
  useEffect(() => {
    if (state.screen === "menu") Audio.openMenu();
    else Audio.closeMenu();
  }, [state.screen]);

  // ── AUDIO: room ambience (hard-cut on room change) ───────────
  // Only beds a room while actually exploring the manor. Star Power
  // and the rave take the stage on their own; AudioManager guards
  // those internally, so it's safe to just announce the room here.
  useEffect(() => {
    if (state.screen === "manor") Audio.changeRoom(state.currentRoom || "hall");
  }, [state.screen, state.currentRoom]);

  // ── AUDIO: leaving the manor silences the house bed ──────────
  useEffect(() => {
    if (state.screen === "dead") Audio.stopHouse();
  }, [state.screen]);

  // ── AUDIO: jumpscare sting (priority-gated) ──────────────────
  // When a real scare takes over, fire the matching sting: the
  // tenant/maw are the real threat, the toy/frame are the
  // masquerade, the crib child is Tome's Shadow. AudioManager sorts
  // out priority if anything is already ringing.
  useEffect(() => {
    if (jumpscare.current) Audio.jumpscareSting(stingForScare(jumpscare.current));
  }, [jumpscare.current]);

  // ── AUDIO: Star Power finale theme (over the heartbeat) ──────
  useEffect(() => {
    if (state.screen !== "manor") return;
    if (starPower) Audio.enterStarPower();
    else Audio.exitStarPower();
  }, [starPower, state.screen]);

  // Checkpoint: snapshot after every collectible secured (psychosis reset).
  // On hardcore death, the game reverts here instead of wiping everything.
  useEffect(() => {
    if (tenantReset > 0 && state.screen === "manor") {
      saveCheckpoint(state);
    }
  }, [tenantReset]); // eslint-disable-line react-hooks/exhaustive-deps

  // A secured collectible is the game's "new day" — the shroom refreshes.
  useEffect(() => {
    if (tenantReset > 0) setMushroomUsed(false);
  }, [tenantReset]);

  const patch = useCallback((p) => setState((s) => ({ ...s, ...p })), []);

  // ── MENU HANDLERS ───────────────────────────────────────────
  const menuNewGame = useCallback(() => {
    wipeSave();
    setBg("dusk");
    setScriptId("prologue");
    setBeatIdx(0);
    setQteFail(null);
    setState((s) => ({ ...newGameState(), settings: s.settings, screen: "story" }));
  }, []);

  const menuSkipPrologue = useCallback(() => {
    setBg("dusk");
    setScriptId("yearsLater");
    setBeatIdx(0);
    setQteFail(null);
    patch({ screen: "story" });
  }, [patch]);

  const menuCursedMode = useCallback(() => {
    wipeSave();
    setBg("dusk");
    setScriptId("yearsLater");
    setBeatIdx(0);
    setQteFail(null);
    // Start a fresh game already in Cursed Mode (skipping prologue)
    setState((s) => ({ ...newGameState(), cursed: true, hardcore: true, settings: s.settings, screen: "story" }));
  }, []);

  const menuContinue = useCallback(() => {
    patch({ screen: state.sawManorIntro ? "manor" : "manor", currentRoom: state.currentRoom || "hall" });
    setTenantReset((n) => n + 1);
  }, [patch, state.sawManorIntro, state.currentRoom]);

  const mergeSettings = useCallback(
    (sp) => setState((s) => ({ ...s, settings: { ...s.settings, ...sp } })),
    []
  );

  // Text from the house stays up until the player presses Next — the
  // game is paused while it reads, so there's no reason to rush it
  // off-screen (per design). No auto-timeout anymore.
  const showToast = useCallback((text, trophy) => {
    setToast({ text, trophy });
  }, []);

  // ── STORY PLAYER ────────────────────────────────────────────
  const startScript = useCallback((id) => {
    setScriptId(id);
    setBeatIdx(0);
    setQteFail(null);
    patch({ screen: "story" });
  }, [patch]);

  // ── Apply a puzzleEngine result to state ────────────────────
  const applyResult = useCallback(
    (r) => {
      if (!r) return;
      setState((s) => {
        const inv = s.inventory
          .filter((i) => !(r.consumeItems || []).includes(i))
          .concat((r.grantItems || []).filter((g) => !s.inventory.includes(g)));
        const flags = { ...s.flags, ...(r.setFlags || {}) };
        const unlocked =
          r.unlockDoor && !s.unlockedDoors.includes(r.unlockDoor)
            ? [...s.unlockedDoors, r.unlockDoor]
            : s.unlockedDoors;
        const trophies = r.trophy && !s.trophies.find((t) => t.id === r.trophy.id)
          ? [...s.trophies, r.trophy]
          : s.trophies;
        const drives = r.addDrive ? applyDrives(s.drives, r.addDrive) : s.drives;
        return { ...s, inventory: inv, flags, unlockedDoors: unlocked, trophies, drives };
      });
      if (r.message) showToast(r.message, r.trophy);
      if (r.scare) fireScare();
      if (r.majorTask) setTenantReset((n) => n + 1); // Tenant loses the scent
      if (r.grantItems?.length || r.consumeItems?.length) setArmedItem(null);
      if (r.startScript) startScript(r.startScript); // e.g. the finale
      // A scare is only ever CONSIDERED at a genuinely tense beat — and
      // even then it rarely lands (see useJumpscareScheduler). Big beats
      // (a key item, a door, a major task) carry more weight than a
      // quiet success. Plain inspects/eggs never reach for one.
      if (r.ok) {
        const big = r.majorTask || r.unlockDoor || r.grantItems?.length;
        if (big) jumpscare.maybeScare(0.85);
      }
    },
    [showToast, startScript, fireScare, jumpscare]
  );

  const beats = scriptId ? SCRIPTS[scriptId] : null;
  const beat = beats ? beats[beatIdx] : null;

  // Process non-interactive beats automatically
  useEffect(() => {
    if (!beat) return;
    if (beat.type === "scene") {
      setBg(beat.bg);
      setBeatIdx((i) => i + 1);
    } else if (beat.type === "set") {
      setState((s) => ({ ...s, [beat.flag]: beat.value }));
      setBeatIdx((i) => i + 1);
    } else if (beat.type === "flag") {
      setState((s) => ({ ...s, flags: { ...s.flags, [beat.flag]: beat.value } }));
      setBeatIdx((i) => i + 1);
    } else if (beat.type === "drive") {
      setState((s) => ({ ...s, drives: applyDrives(s.drives, beat.add) }));
      setBeatIdx((i) => i + 1);
    } else if (beat.type === "decideEnding") {
      // Hand off to the branching brain. It reads drives + flags +
      // Cursed Mode (never altering the timer) and picks an ending block.
      setState((s) => {
        // Path A detection: if we're under the curse AND the player has
        // already completed some non-canon ending, this cursed run counts
        // as a recreation — flip canonViaRecreation so the engine routes
        // to the true ending. (Path B — plain Cursed Mode — already
        // qualifies via the canon ending's own test.)
        const recreation =
          s.cursed &&
          s.completedEndings &&
          Object.keys(s.completedEndings).some(
            (id) => id !== "endWrongHouse" && s.completedEndings[id]
          );
        const seeded = recreation ? { ...s, canonViaRecreation: true } : s;
        const { scriptId: endId, endingId } = decideEnding(seeded);
        // jump on the next tick so state (e.g. toll_paid) is committed
        setTimeout(() => {
          setScriptId(endId);
          setBeatIdx(0);
        }, 0);
        // Remember which ending resolved + persist the recreation flag so
        // the `end` beat can record completion against the right id.
        return { ...seeded, lastEndingId: endingId };
      });
    } else if (beat.type === "trophy") {
      setState((s) =>
        s.trophies.find((t) => t.id === beat.trophy.id)
          ? s
          : { ...s, trophies: [...s.trophies, beat.trophy] }
      );
      showToast(beat.trophy.desc, beat.trophy);
      setBeatIdx((i) => i + 1);
    } else if (beat.type === "goto") {
      setScriptId(beat.target);
      setBeatIdx(0);
    } else if (beat.type === "end") {
      // If we just finished an ending block, record completion so the
      // canon true ending + a future "endings/tones discovered" screen
      // know what the player has reached. Harmless on non-ending ends
      // (lastEndingId is only set by the decideEnding handler).
      if (state.lastEndingId) {
        setState((s) =>
          s.lastEndingId
            ? { ...s, ...recordEnding(s, s.lastEndingId), lastEndingId: null }
            : s
        );
      }
      setScriptId(null);
      setBg("manor");
      if (beat.next === "manor") {
        if (!state.sawManorIntro) {
          patch({ sawManorIntro: true });
          startScript("manorIntro");
          setBg("manor");
        } else {
          patch({ screen: "manor", currentRoom: "hall" });
        }
      }
    }
  }, [beat, patch, startScript, showToast, state.sawManorIntro, state.lastEndingId]);

  const advance = useCallback(() => setBeatIdx((i) => i + 1), []);

  // keyboard advance for lines
  useEffect(() => {
    if (!beat || beat.type !== "line") return undefined;
    const onKey = (e) => {
      if (e.code === "Space" || e.key === "Enter") advance();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [beat, advance]);

  // ── DEATH ───────────────────────────────────────────────────
  const die = useCallback(
    (cause) => {
      setScareFlash(true);
      setTimeout(() => setScareFlash(false), 450);
      setState((s) => {
        const dead = { ...s, deaths: s.deaths + 1, screen: "dead" };
        if (s.hardcore && s.screen === "manor") {
          // Checkpoint system: revert to last collectible instead of
          // wiping everything. If no checkpoint yet (died before your
          // first collectible), THEN the curse takes it all.
          const cp = loadCheckpoint();
          if (cp) {
            return { ...cp, deaths: s.deaths + 1, screen: "dead", _checkpoint: true };
          }
          wipeSave();
          return { ...newGameState(), screen: "dead", deaths: s.deaths + 1, hardcore: true, _wiped: true };
        }
        return dead;
      });
      setQteFail(cause || null);
      setRetryScript(scriptId);
      setScriptId(null);
    },
    [scriptId]
  );

  // ── JUMPSCARE RESOLUTION (tier-driven lethality) ─────────────
  // Surviving a scare just dismisses it (the scheduler re-arms). If a
  // scare LANDS (wrong key / ran out of time), whether it's fatal
  // depends on the current psychosis tier: calm scares rarely kill,
  // breaking-tier scares usually do. Masquerade scares (toy, frame)
  // are never lethal regardless of tier — they're the hype-man beats.
  const onScareSurvive = useCallback(() => {
    jumpscare.survive();
  }, [jumpscare]);

  const onScareCaught = useCallback(() => {
    const c = jumpscare.caught(); // clears overlay, returns the scare
    if (!c) return;
    const cfg = PSY_TIERS[psyTier] || PSY_TIERS[0];
    const canKill = c.kind === "monster";
    const lethal = canKill && Math.random() < cfg.lethalChance;
    if (lethal) {
      die(c.death || "It reached you. The house keeps what it catches.");
    } else {
      // a non-fatal hit: the sting flash + the death-line as a near-miss
      fireScare();
      showToast(c.death || "It brushed past you. Next time it won't miss.");
    }
  }, [jumpscare, psyTier, die, fireScare, showToast]);

  // ── MANOR HANDLERS ──────────────────────────────────────────
  const room = ROOMS[state.currentRoom] || ROOMS.hall;

  const onAction = useCallback(
    (action, hotspot, itemId) => {
      if (action === "take" && hotspot.item) {
        const flag = hotspot.takeFlag || `took_${hotspot.id}`;
        if (state.flags[flag]) {
          applyResult({ ok: false, message: "You already took what this was holding." });
          return;
        }
        applyResult({
          ok: true,
          message: `Taken: ${ITEMS[hotspot.item].name}. ${ITEMS[hotspot.item].desc}`,
          grantItems: [hotspot.item],
          setFlags: { [flag]: true },
        });
      } else if (action === "press") {
        applyResult(pressHotspot(hotspot, state));
      } else if (action === "useItem" && itemId) {
        // Picker- or armed-driven: use a specific item on this hotspot.
        applyResult(resolveUse(itemId, hotspot.id, state));
      } else if (action === "use" && armedItem) {
        applyResult(resolveUse(armedItem, hotspot.id, state));
      }
    },
    [applyResult, armedItem, state]
  );

  const onDoor = useCallback(
    (doorId) => {
      const r = tryDoor(doorId, state);
      applyResult(r);
      if (r.ok && r.enterRoom) patch({ currentRoom: r.enterRoom });
    },
    [applyResult, patch, state]
  );

  // In-world choices (cruelty/mercy/curiosity/etc) seed drives → endings
  const onChoice = useCallback(
    (choiceId) => {
      // ── MUSHROOM EASTER EGG ──────────────────────────────────
      // Eat the ring while you're in Star Power: a 2-minute rave.
      // Psychosis freezes for the party, then clears — fully in
      // regular play, HALF under the curse. Once per day-cycle
      // (refreshes when you next secure a collectible).
      if (choiceId === "eat_mushrooms") {
        if (raving) return;
        if (!starPower) {
          showToast("The caps just lean toward the Mouth, waiting. Nothing happens. (Not yet.)");
          return;
        }
        if (mushroomUsed) {
          showToast("You've already eaten from the ring this cycle. The caps have nothing left to give — for now.");
          return;
        }
        setMushroomUsed(true);
        setRaving(true);
        showToast("You eat from the ring.\n\nColor floods in. The veins in the walls light up like a stadium, pulsing rainbow, and your own heartbeat becomes a drumline. For two minutes, the house is a PARTY and nothing can touch you.");
        Audio.mushroomRave().then(() => {
          setRaving(false);
          if (state.cursed) {
            // Half-clear under the curse.
            setPsyReduce((r) => ({ n: r.n + 1, frac: 0.5 }));
          } else {
            // Full clear in regular play (same as a secured collectible's relief).
            setTenantReset((n) => n + 1);
          }
        });
        return;
      }
      applyResult(resolveChoice(choiceId, state));
    },
    [applyResult, state, raving, starPower, mushroomUsed, showToast]
  );

  // ── RENDER ──────────────────────────────────────────────────
  const backdrop = useMemo(
    () => ({ ...S.app, background: BGS[state.screen === "manor" ? "manor" : bg] }),
    [bg, state.screen]
  );

  return (
    <div style={backdrop}>
      {scareFlash && <div style={S.flash} className="scare-flash" />}

      {/* ── MENU ── */}
      {state.screen === "menu" && (
        <MainMenu
          hasSave={hasSave}
          save={state}
          onNewGame={menuNewGame}
          onSkipPrologue={menuSkipPrologue}
          onCursedMode={menuCursedMode}
          onContinue={menuContinue}
          settings={state.settings}
          onSettings={mergeSettings}
          onDevUnlockEyes={(on) =>
            setState((s) => ({ ...s, flags: { ...s.flags, unstableEyes: on } }))
          }
          onDevStarPower={setStarPower}
          devStarPower={starPower}
        />
      )}

      {/* ── TITLE ── */}
      {state.screen === "title" && (
        <div style={S.center}>
          <div style={S.titleSmall}>an ILLEGAL EMPIRE digital novel</div>
          <div style={S.titleBig} className="mouth-breathe">HOUSE of CARDS</div>
          <div style={S.titleSub}>the ground was burned for a reason</div>
          <div style={{ display: "flex", gap: 14, marginTop: 36 }}>
            <button style={S.bigBtn} onClick={() => startScript("prologue")}>
              New Game
            </button>
            <button
              style={S.bigBtnDim}
              onClick={() => startScript("yearsLater")}
              title="Skip the fire. Jump to the deal."
            >
              Skip Prologue
            </button>
            {state.unlockedDoors.length > 0 && (
              <button style={S.bigBtnDim} onClick={() => patch({ screen: "manor" })}>
                Continue
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── STORY ── */}
      {state.screen === "story" && beat && (
        <div style={S.center}>
          {beat.type === "line" && (
            <div style={S.lineBox} onClick={advance}>
              {beat.speaker && <div style={S.speaker}>{beat.speaker}</div>}
              <div style={S.lineText}>{beat.text}</div>
              <div style={S.continueHint}>space / click ▸</div>
            </div>
          )}

          {beat.type === "qte" && (
            <QTE
              key={`${scriptId}-${beatIdx}`}
              {...beat.qte}
              onResult={(ok) => {
                if (ok) advance();
                else die(beat.failText);
              }}
            />
          )}

          {beat.type === "choice" && (
            <div style={S.lineBox}>
              <div style={S.lineText}>{beat.prompt}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 18 }}>
                {beat.options.map((o) => (
                  <button
                    key={o.label}
                    style={S.choiceBtn}
                    onClick={() => {
                      // a choice may seed drives and/or set a flag the moment it's picked
                      if (o.addDrive || o.flag) {
                        setState((s) => ({
                          ...s,
                          drives: o.addDrive ? applyDrives(s.drives, o.addDrive) : s.drives,
                          flags: o.flag ? { ...s.flags, [o.flag]: true } : s.flags,
                        }));
                      }
                      setScriptId(o.goto);
                      setBeatIdx(0);
                    }}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── MANOR ── */}
      {state.screen === "manor" && (
        <>
          {/* The psychosis meter is the house's clock now — and it runs in
              EVERY mode. Easy Mode is not safe; the mind can still break.
              Cursed Mode just leans on the dial: time bleeds faster. This
              is the Tenant timer wearing the mask of Tome's own unraveling;
              "collectible secured" (majorTask → tenantReset) empties it. */}
          <PsychosisEngine
            resetSignal={tenantReset}
            reduceSignal={psyReduce}
            paused={!!jumpscare.current || overlayOpen || !!toast || raving}
            rate={
              // Base rate: 1. Cursed Mode: 1.8x. Unstable Eyes: 2x.
              // These stack — Cursed + Eyes = 3.6x. The Wendigo and the
              // Raven lean on the same dial; seeing the truth costs sanity.
              (state.cursed ? 1.8 : 1) * (eyesOn ? 2 : 1)
            }
            onTier={setPsyTier}
            onPct={(pct) => {
              setPsyPct(pct);
              Audio.setHeartbeatTier(heartbeatTierForPct(pct));
            }}
            onBreak={() =>
              die(
                state.cursed
                  ? "You can't tell the house from your own head anymore. Something in you gives."
                  : "The walls won't hold still. Neither will you. Your mind lets go."
              )
            }
          />
          <SceneEngine
            room={room}
            state={state}
            armedItem={armedItem}
            eyesActive={!!state.flags.unstableEyes && eyesOn}
            cursed={!!state.cursed}
            onAction={onAction}
            onDoor={onDoor}
            onChoice={onChoice}
            onScare={fireScare}
            onMaybeScare={(w) => jumpscare.maybeScare(w)}
            onArm={setArmedItem}
            onOverlayChange={setOverlayOpen}
            onLeave={() => { setOverlayOpen(false); patch({ currentRoom: "hall" }); }}
          />
          <Inventory inventory={state.inventory} armedItem={armedItem} onArm={setArmedItem} hidden />
          {state.hardcore && <div style={S.hardcoreBadge}>☠ HARDCORE</div>}

          {/* ── Persistent MENU button (always reachable, item 1) ────
              Pinned top-left, above everything. Lets the player drop
              back to the front of house at any time — pockets, settings,
              continue all live there. */}
          <button
            style={S.menuBtn}
            onClick={() => { setOverlayOpen(false); setToast(null); patch({ screen: "menu" }); }}
            title="Back to the menu"
          >
            ☰ MENU
          </button>

          {/* ── Persistent Unstable Eyes toggle (after unlock) ──────
              Once the lullaby opens Tome's sight, this stays on screen.
              Tapping it flips the view: objects that only exist in the
              unstable world appear; false comforts dissolve. No cost. */}
          {state.flags.unstableEyes && (
            <button
              style={{
                ...S.eyesBtn,
                ...(eyesOn ? S.eyesBtnOn : {}),
              }}
              onClick={() => setEyesOn((v) => !v)}
              title={eyesOn ? "Close your eyes to the truth" : "Open your eyes to the truth"}
            >
              <span style={S.eyesGlyph}>{eyesOn ? "◉" : "◌"}</span>
              {eyesOn ? "UNSTABLE EYES — OPEN" : "UNSTABLE EYES"}
            </button>
          )}

          {/* ── The real jumpscare overlay (SCARE_BANK) ──────────── */}
          {jumpscare.current && (
            <Jumpscare
              scare={jumpscare.current}
              cursed={!!state.cursed}
              onSurvive={onScareSurvive}
              onCaught={onScareCaught}
            />
          )}
        </>
      )}

      {/* ── DEAD ── */}
      {state.screen === "dead" && (
        <div style={S.center}>
          <div style={{ ...S.titleBig, color: RM.fleshGlow }}>YOU DIED</div>
          {qteFail && <div style={S.deathText}>{qteFail}</div>}
          {state._wiped ? (
            <>
              <div style={S.deathText}>The curse keeps what it kills. Your save is gone.</div>
              <button style={S.bigBtn} onClick={() => { setState((s) => ({ ...newGameState(), settings: s.settings })); setBg("dusk"); }}>
                Return to Menu
              </button>
            </>
          ) : state._checkpoint ? (
            <>
              <div style={S.deathText}>The house dragged you back to the last thing you held onto.</div>
              <button
                style={S.bigBtn}
                onClick={() => {
                  patch({ screen: "manor", currentRoom: "hall", _checkpoint: false });
                  setTenantReset((n) => n + 1);
                }}
              >
                Wake at the checkpoint
              </button>
            </>
          ) : (
            <button
              style={S.bigBtn}
              onClick={() => {
                if (retryScript) {
                  startScript(retryScript);
                  setRetryScript(null);
                } else if (state.unlockedDoors.length > 0 || state.sawManorIntro) {
                  patch({ screen: "manor", currentRoom: "hall" });
                  setTenantReset((n) => n + 1);
                } else {
                  patch({ screen: "menu" });
                }
              }}
            >
              {retryScript ? "Try Again" : "Wake up in the Hall"}
            </button>
          )}
        </div>
      )}

      {/* ── HOUSE TEXT (centered, paused, stays until Next) ── */}
      {toast && (
        <div style={S.toastVeil} onClick={() => setToast(null)}>
          <div
            style={{ ...S.toast, ...(toast.trophy ? S.trophyToast : {}) }}
            onClick={(e) => e.stopPropagation()}
          >
            {toast.trophy && <div style={S.trophyTitle}>🏆 {toast.trophy.name}</div>}
            <div style={S.toastText}>{toast.text}</div>
            <button style={S.toastNext} onClick={() => setToast(null)}>
              Next ▸
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  app: {
    position: "fixed",
    inset: 0,
    overflow: "hidden",
    fontFamily: RM.font,
  },
  flash: {
    position: "fixed",
    inset: 0,
    background: RM.fleshDeep,
    zIndex: 90,
    pointerEvents: "none",
  },
  center: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: 20,
  },
  titleSmall: { fontFamily: RM.fontD, fontSize: 13, letterSpacing: 5, color: RM.boneFaint },
  titleBig: {
    fontFamily: RM.fontD,
    fontSize: "clamp(40px, 8vw, 76px)",
    letterSpacing: 6,
    color: RM.bone,
    textShadow: `0 0 40px ${RM.vein}, 0 0 80px ${RM.fleshDeep}`,
    textAlign: "center",
  },
  titleSub: { fontFamily: RM.font, fontStyle: "italic", fontSize: 16, color: RM.boneDim },
  bigBtn: {
    padding: "13px 30px",
    background: RM.fleshDeep,
    border: `1px solid ${RM.fleshGlow}`,
    borderRadius: 10,
    color: RM.bone,
    fontFamily: RM.fontD,
    fontSize: 17,
    letterSpacing: 2,
    cursor: "pointer",
    boxShadow: emberGlow(RM.vein),
  },
  bigBtnDim: {
    padding: "13px 30px",
    background: "transparent",
    border: `1px solid ${RM.charBorder}`,
    borderRadius: 10,
    color: RM.boneDim,
    fontFamily: RM.fontD,
    fontSize: 17,
    letterSpacing: 2,
    cursor: "pointer",
  },
  lineBox: {
    maxWidth: 620,
    width: "92vw",
    padding: "28px 32px",
    background: "rgba(7,5,4,0.86)",
    border: `1px solid ${RM.charBorder}`,
    borderRadius: 12,
    cursor: "pointer",
  },
  speaker: {
    fontFamily: RM.fontD,
    fontSize: 14,
    letterSpacing: 3,
    color: RM.ember,
    marginBottom: 10,
  },
  lineText: { fontFamily: RM.font, fontSize: 19, lineHeight: 1.6, color: RM.bone, whiteSpace: "pre-wrap" },
  continueHint: {
    marginTop: 16,
    textAlign: "right",
    fontSize: 12,
    color: RM.boneFaint,
    fontFamily: RM.fontD,
    letterSpacing: 2,
  },
  choiceBtn: {
    padding: "13px 18px",
    background: RM.char2,
    border: `1px solid ${RM.vein}`,
    borderRadius: 9,
    color: RM.bone,
    fontFamily: RM.font,
    fontSize: 16,
    cursor: "pointer",
    textAlign: "left",
  },
  deathText: {
    maxWidth: 520,
    textAlign: "center",
    fontFamily: RM.font,
    fontStyle: "italic",
    fontSize: 17,
    color: RM.boneDim,
    lineHeight: 1.6,
    marginBottom: 14,
  },
  hardcoreBadge: {
    position: "fixed",
    top: 60,
    right: 14,
    padding: "5px 12px",
    background: "rgba(92,31,36,0.5)",
    border: `1px solid ${RM.fleshGlow}`,
    borderRadius: 7,
    fontFamily: RM.fontD,
    fontSize: 12,
    letterSpacing: 2,
    color: RM.fleshGlow,
    zIndex: 50,
  },
  menuBtn: {
    position: "fixed",
    top: 60,
    left: 14,
    padding: "8px 14px",
    background: "rgba(13,9,7,0.92)",
    border: `1px solid ${RM.charBorder}`,
    borderRadius: 10,
    color: RM.boneDim,
    fontFamily: RM.fontD,
    fontSize: 13,
    letterSpacing: 2,
    cursor: "pointer",
    zIndex: 58,
  },
  eyesBtn: {
    position: "fixed",
    bottom: 14,
    right: 14,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "9px 14px",
    background: "rgba(13,9,7,0.92)",
    border: `1px solid ${RM.charBorder}`,
    borderRadius: 10,
    color: RM.boneDim,
    fontFamily: RM.fontD,
    fontSize: 12,
    letterSpacing: 2,
    cursor: "pointer",
    zIndex: 58,
    transition: "all 180ms ease",
  },
  eyesBtnOn: {
    background: "linear-gradient(180deg, rgba(86,20,64,0.95), rgba(40,8,32,0.95))",
    border: `1px solid ${RM.fleshGlow}`,
    color: "#f0c8d8",
    boxShadow: `0 0 14px rgba(196,86,90,0.5), 0 0 28px rgba(96,24,120,0.4)`,
  },
  eyesGlyph: { fontSize: 16, lineHeight: 1 },
  toastVeil: {
    position: "fixed",
    inset: 0,
    background: "rgba(5,3,2,0.74)",
    backdropFilter: "blur(2px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 70,
    padding: 20,
  },
  toast: {
    maxWidth: 560,
    width: "100%",
    padding: "24px 26px",
    background: "rgba(13,9,7,0.98)",
    border: `1px solid ${RM.charBorder}`,
    borderRadius: 14,
    boxShadow: "0 30px 80px rgba(0,0,0,0.7)",
  },
  trophyToast: { borderColor: RM.emberBright, boxShadow: emberGlow() },
  trophyTitle: {
    fontFamily: RM.fontD,
    fontSize: 18,
    color: RM.emberBright,
    marginBottom: 10,
  },
  toastText: {
    fontFamily: RM.font,
    fontSize: 17,
    color: RM.bone,
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
  },
  toastNext: {
    marginTop: 18,
    padding: "10px 22px",
    background: RM.fleshDeep,
    border: `1px solid ${RM.fleshGlow}`,
    borderRadius: 9,
    color: RM.bone,
    fontFamily: RM.fontD,
    fontSize: 15,
    letterSpacing: 2,
    cursor: "pointer",
    float: "right",
  },
};
